/**
 * Menu 2 · Submenu D: Teambeheer & Bevoegdheden (exclusief OWNER, migratie 150).
 * Pop-up modal: naam + e-mail + rol (OWNER / ADMINISTRATOR / TRAINER) en voor een
 * TRAINER de klassen; direct verwerkt in provider_team_members.
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { UserPlus, X, Trash2, Crown, ShieldCheck, GraduationCap, Mail, Clock } from 'lucide-react';
import { getTeam, nodigTeamlidUit, wijzigTeamlid, verwijderTeamlid, TeamLid } from '../services/opleiderCockpitApi';
import type { OpleiderRol } from '../components/opleiderCockpit/opleiderMenu';
import { useAuthStore } from '../store/authStore';

const ROL_ICON: Record<OpleiderRol, React.ComponentType<{ className?: string }>> = { OWNER: Crown, ADMINISTRATOR: ShieldCheck, TRAINER: GraduationCap };
const ROL_KLEUR: Record<OpleiderRol, string> = {
  OWNER: 'bg-amber-50 text-amber-800 border-amber-200',
  ADMINISTRATOR: 'bg-sky-50 text-sky-800 border-sky-200',
  TRAINER: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};
const STATUS_KLEUR: Record<TeamLid['status'], string> = {
  actief: 'bg-green-100 text-green-700',
  uitgenodigd: 'bg-blue-100 text-blue-700',
  verlopen: 'bg-red-100 text-red-700',
  inactief: 'bg-gray-100 text-gray-500',
};

function foutMelding(e: unknown, fallback: string) {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;
}

const UitnodigModal: React.FC<{
  trainingen: Array<{ id: number; titel: string }>;
  bewerk?: TeamLid | null;
  onClose: () => void;
}> = ({ trainingen, bewerk, onClose }) => {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const [naam, setNaam] = useState(bewerk?.naam ?? '');
  const [email, setEmail] = useState(bewerk?.email ?? '');
  const [rol, setRol] = useState<OpleiderRol>(bewerk?.rol ?? 'TRAINER');
  const [klassen, setKlassen] = useState<number[]>(bewerk?.trainingen.map((x) => x.id) ?? []);
  const [fout, setFout] = useState<string | null>(null);

  const opslaan = useMutation({
    mutationFn: async () => {
      if (bewerk?.id) return wijzigTeamlid(bewerk.id, { rol, training_ids: rol === 'TRAINER' ? klassen : [] });
      return nodigTeamlidUit({ naam, email, rol, training_ids: rol === 'TRAINER' ? klassen : [] });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['opleider-team'] }); onClose(); },
    onError: (e) => setFout(foutMelding(e, t('opleiderCockpit.team.fout'))),
  });

  const toggle = (id: number) => setKlassen((k) => (k.includes(id) ? k.filter((x) => x !== id) : [...k, id]));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); setFout(null); opslaan.mutate(); }} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{bewerk ? t('opleiderCockpit.team.bewerkTitel') : t('opleiderCockpit.team.uitnodigTitel')}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100" aria-label={t('opleiderCockpit.sluiten')}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-naam">{t('opleiderCockpit.team.naam')}</label>
            <input id="team-naam" required minLength={2} disabled={!!bewerk} value={naam} onChange={(e) => setNaam(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="team-email">{t('opleiderCockpit.team.email')}</label>
            <input id="team-email" type="email" required disabled={!!bewerk} value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg disabled:bg-gray-50" />
          </div>
          <fieldset>
            <legend className="block text-sm font-medium text-gray-700 mb-2">{t('opleiderCockpit.team.rol')}</legend>
            <div className="grid gap-2">
              {(['OWNER', 'ADMINISTRATOR', 'TRAINER'] as OpleiderRol[]).map((r) => {
                const Icon = ROL_ICON[r];
                return (
                  <label key={r} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${rol === r ? 'border-[#0B1F3A] bg-blue-50/40' : 'border-gray-200 hover:bg-gray-50'}`}>
                    <input type="radio" name="rol" value={r} checked={rol === r} onChange={() => setRol(r)} className="mt-1" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 flex items-center gap-1.5"><Icon className="w-4 h-4" />{t(`opleiderCockpit.team.rollen.${r}.label`)}</p>
                      <p className="text-xs text-gray-500">{t(`opleiderCockpit.team.rollen.${r}.uitleg`)}</p>
                    </div>
                  </label>
                );
              })}
            </div>
          </fieldset>
          {rol === 'TRAINER' && (
            <fieldset>
              <legend className="block text-sm font-medium text-gray-700 mb-2">{t('opleiderCockpit.team.klassen')}</legend>
              {trainingen.length === 0 ? (
                <p className="text-sm text-gray-500">{t('opleider.noTrainingsYet')}</p>
              ) : (
                <div className="max-h-44 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {trainingen.map((tr) => (
                    <label key={tr.id} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                      <input type="checkbox" checked={klassen.includes(tr.id)} onChange={() => toggle(tr.id)} />
                      <span className="truncate">{tr.titel}</span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>
          )}
          {fout && <p className="text-sm text-red-600" role="alert">{fout}</p>}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">{t('opleiderCockpit.annuleren')}</button>
          <button type="submit" disabled={opslaan.isPending} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1F3A] text-white text-sm font-medium hover:bg-[#1A3459] disabled:opacity-60">
            {bewerk ? t('opleiderCockpit.opslaan') : <><Mail className="w-4 h-4" />{t('opleiderCockpit.team.verstuur')}</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export const OpleiderTeamPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const [modal, setModal] = useState<null | { bewerk?: TeamLid }>(null);
  const [fout, setFout] = useState<string | null>(null);
  const { data, isLoading } = useQuery({ queryKey: ['opleider-team'], queryFn: getTeam });

  const verwijder = useMutation({
    mutationFn: verwijderTeamlid,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['opleider-team'] }),
    onError: (e) => setFout(foutMelding(e, t('opleiderCockpit.team.fout'))),
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-500 max-w-2xl">{t('opleiderCockpit.team.intro')}</p>
        <button type="button" onClick={() => setModal({})} className="inline-flex items-center gap-2 bg-[#0B1F3A] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#1A3459]">
          <UserPlus className="w-4 h-4" />{t('opleiderCockpit.team.nodigUit')}
        </button>
      </div>
      {fout && <p className="mb-4 text-sm text-red-600" role="alert">{fout}</p>}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-x-auto">
        {isLoading || !data ? (
          <div className="p-8 text-center"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-5 py-3">{t('opleiderCockpit.team.naam')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.team.rol')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.team.klassen')}</th>
                <th className="px-5 py-3">{t('opleiderCockpit.roster.status')}</th>
                <th className="px-5 py-3 text-right">{t('opleiderCockpit.roster.acties')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.leden.map((lid) => {
                const Icon = ROL_ICON[lid.rol];
                const isIk = lid.user_id === user?.id;
                return (
                  <tr key={lid.id ?? 'eigenaar'} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <p className="font-medium text-gray-900">{lid.naam}{isIk && <span className="ml-2 text-xs text-gray-400">({t('opleiderCockpit.team.jij')})</span>}</p>
                      <p className="text-xs text-gray-500">{lid.email}</p>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${ROL_KLEUR[lid.rol]}`}><Icon className="w-3.5 h-3.5" />{t(`opleiderCockpit.team.rollen.${lid.rol}.label`)}</span>
                      {lid.is_hoofdeigenaar && <span className="block text-[11px] text-gray-400 mt-0.5">{t('opleiderCockpit.team.hoofdeigenaar')}</span>}
                    </td>
                    <td className="px-5 py-3 text-gray-600">
                      {lid.rol !== 'TRAINER' ? <span className="text-xs text-gray-400">{t('opleiderCockpit.team.alleKlassen')}</span>
                        : lid.trainingen.length === 0 ? <span className="text-xs text-amber-600">{t('opleiderCockpit.team.geenKlassen')}</span>
                        : <span className="text-xs">{lid.trainingen.map((x) => x.titel).join(', ')}</span>}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_KLEUR[lid.status]}`}>{t(`opleiderCockpit.team.status.${lid.status}`)}</span>
                      {lid.status === 'uitgenodigd' && lid.invite_token_expires && (
                        <span className="block text-[11px] text-gray-400 mt-0.5 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(lid.invite_token_expires).toLocaleDateString('nl-NL')}</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {!lid.is_hoofdeigenaar && lid.id && (
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => setModal({ bewerk: lid })} className="px-3 py-1.5 rounded-lg text-xs font-medium text-[#0B1F3A] hover:bg-blue-50">{t('opleiderCockpit.team.wijzig')}</button>
                          <button type="button" disabled={verwijder.isPending}
                            onClick={() => { if (window.confirm(t('opleiderCockpit.team.bevestigVerwijder', { naam: lid.naam }))) verwijder.mutate(lid.id!); }}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50" title={t('opleiderCockpit.team.verwijder')}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {modal && data && <UitnodigModal trainingen={data.trainingen} bewerk={modal.bewerk} onClose={() => setModal(null)} />}
    </div>
  );
};

export default OpleiderTeamPage;
